// «TESTCS Look» — фирменный вид мира (слово Дениса 2026-08-05: «игра выглядит
// убого, нужна своя графика»).
//
// Что делает и почему именно это:
//  1. КОНТУР по глубине. Аватары рисуются MToon'ом с плоской заливкой, а мир
//     до сих пор читался как «серые коробки» — не хватало линии. Контур по
//     разрыву глубины даёт рисованную обводку силуэтов и рёбер, и делает это
//     на любой геометрии, ничего не перестраивая.
//  2. ГРЕЙДИНГ. Тени уводим в холод, света — в тепло, слегка поднимаем
//     насыщенность. Именно расхождение теней и светов по температуре убирает
//     «молоко», которым мир выглядел на дневном цикле.
//  3. ВИНЬЕТКА — мягкая, только чтобы собрать кадр к центру.
//
// Порог контура растёт с расстоянием: иначе дальние объекты залило бы сеткой
// линий, а вблизи контур бы пропадал. Сила линии гаснет к горизонту — линия
// на небе и на дальних островах читается как грязь.
import { BlendFunction, Effect, EffectAttribute } from 'postprocessing'
import * as THREE from './three'

const fragmentShader = /* glsl */ `
uniform float uOutlineStrength;
uniform float uOutlineScale;
uniform float uGradeStrength;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform float uSaturation;
uniform float uVignette;

// Глубина в линейных единицах вида: сравнивать «сырые» значения бессмысленно,
// они нелинейны и у дальних объектов почти одинаковы.
float linearDepthAt(const in vec2 uv) {
  float raw = readDepth(uv);
  return viewZToOrthographicDepth(getViewZ(raw), cameraNear, cameraFar);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
  vec3 color = inputColor.rgb;

  // ── контур: крест из четырёх соседей ──────────────────────────────────────
  vec2 texel = texelSize * uOutlineScale;
  float center = linearDepthAt(uv);
  float left = linearDepthAt(uv - vec2(texel.x, 0.0));
  float right = linearDepthAt(uv + vec2(texel.x, 0.0));
  float down = linearDepthAt(uv - vec2(0.0, texel.y));
  float up = linearDepthAt(uv + vec2(0.0, texel.y));
  float diff = abs(center - left) + abs(center - right) + abs(center - down) + abs(center - up);

  // Порог пропорционален глубине: близкие рёбра ловим тонко, дальние — грубо.
  float threshold = mix(0.0016, 0.010, center);
  float edge = smoothstep(threshold, threshold * 3.4, diff);
  // Небо (глубина у единицы) и дальний план контуром не трогаем.
  float distanceFade = 1.0 - smoothstep(0.55, 0.96, center);
  edge *= distanceFade * uOutlineStrength;
  color *= (1.0 - edge * 0.82);

  // ── грейдинг: холодные тени, тёплые света ────────────────────────────────
  // Яркость нормализуем по Рейнхарду: кадр может прийти и в HDR (эффект стоит
  // до тонмаппинга), и в LDR — порог на «сырой» luma в первом случае врёт и
  // красит всю ночную сцену одним цветом.
  float rawLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
  float luma = rawLuma / (1.0 + rawLuma);
  vec3 tint = mix(uShadowTint, uHighlightTint, smoothstep(0.14, 0.55, luma));
  vec3 graded = color * tint;
  graded = mix(vec3(luma), graded, uSaturation);
  color = mix(color, graded, uGradeStrength);

  // ── виньетка ─────────────────────────────────────────────────────────────
  vec2 centered = uv - 0.5;
  float vignette = 1.0 - dot(centered, centered) * uVignette;
  color *= clamp(vignette, 0.0, 1.0);

  outputColor = vec4(color, inputColor.a);
}
`

export class TestcsLookEffect extends Effect {
  constructor({
    // Подобрано глазами на проде: при 1.0 линия почти не читается, при 3.2
    // персонаж получает жирную двойную обводку.
    outlineStrength = 2.0,
    outlineScale = 1.25,
    // Грейдинг намеренно сдержанный: на ночной сцене сильный сдвиг красит весь
    // кадр в один цвет — проверено глазами на проде 2026-08-05.
    gradeStrength = 0.42,
    shadowTint = new THREE.Color(0.95, 0.98, 1.05),
    highlightTint = new THREE.Color(1.05, 1.0, 0.94),
    saturation = 1.1,
    vignette = 0.26,
  } = {}) {
    super('TestcsLookEffect', fragmentShader, {
      blendFunction: BlendFunction.NORMAL,
      attributes: EffectAttribute.DEPTH,
      uniforms: new Map([
        ['uOutlineStrength', new THREE.Uniform(outlineStrength)],
        ['uOutlineScale', new THREE.Uniform(outlineScale)],
        ['uGradeStrength', new THREE.Uniform(gradeStrength)],
        ['uShadowTint', new THREE.Uniform(new THREE.Vector3(shadowTint.r, shadowTint.g, shadowTint.b))],
        ['uHighlightTint', new THREE.Uniform(new THREE.Vector3(highlightTint.r, highlightTint.g, highlightTint.b))],
        ['uSaturation', new THREE.Uniform(saturation)],
        ['uVignette', new THREE.Uniform(vignette)],
      ]),
    })
  }

  setOutlineStrength(value) {
    this.uniforms.get('uOutlineStrength').value = value
  }

  setGradeStrength(value) {
    this.uniforms.get('uGradeStrength').value = value
  }
}
